import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { CUTTYSARK_STAGES, type CuttysarkStage } from '@cortex/domain-product-innovation-lab-types';
import { useCreateCuttysarkSession, useCuttysarkSessions } from '../hooks/useCuttysark.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞（四半期で最も成果を出した人の表彰）の予選・本戦セッション一覧。参加・運営画面への入口と、リーダー以上がセッションを作る入口を置く
 * @graph-connects useCuttysarkSessions [calls] セッション一覧の取得
 * @graph-connects useCreateCuttysarkSession [calls] セッションの作成
 */
export const Route = createFileRoute('/cuttysark/')({
  component: CuttysarkListRoute,
});

/**
 * ページ単位のガード（canAccessPage）は掛けない。PDG 以外の兼務者も、登録されている回は
 * 開けるようにするため、見られるかどうかは API（canViewCuttysarkSession）に任せ、
 * 403 のときは API のメッセージをそのまま出す。メニューは PDG・admin にだけ出る。
 * @graph-connects CuttysarkListPage [calls] ページ本体
 */
function CuttysarkListRoute(): ReactNode {
  return <CuttysarkListPage />;
}

/** @graph-connects none */
function CuttysarkListPage(): ReactNode {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useCuttysarkSessions();
  const [composing, setComposing] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            🏆 {t('cuttysark.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('cuttysark.intro')}</p>
        </div>
        {data?.canCreate === true && !composing && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setComposing(true);
            }}
          >
            {t('cuttysark.create.heading')}
          </Button>
        )}
      </div>

      {composing && (
        <CreateSessionForm
          onCancel={() => {
            setComposing(false);
          }}
        />
      )}

      {isLoading && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.common.loading')}</p>
      )}
      {isError && (
        <div className="flex items-center gap-2 text-sm text-[var(--color-error)]">
          {t('cuttysark.common.loadError')}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              void refetch();
            }}
          >
            {t('cuttysark.common.retry')}
          </Button>
        </div>
      )}
      {data !== undefined && data.sessions.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.list.empty')}</p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {data?.sessions.map((session) => (
          <Card key={session.id}>
            <CardContent className="space-y-2 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="info">{t(`cuttysark.stage.${session.stage}`)}</Badge>
                <Badge>{t(`cuttysark.phase.${session.phase}`)}</Badge>
                <span className="text-xs text-[var(--color-text-muted)]">{session.quarter}</span>
              </div>
              <p className="font-semibold text-[var(--color-text-primary)]">{session.title}</p>
              <p className="text-xs text-[var(--color-text-muted)]">
                {t('cuttysark.list.candidates', { count: session.candidateCount })}
              </p>
              <div className="flex gap-2">
                <Link to="/cuttysark/$sessionId" params={{ sessionId: session.id }}>
                  <Button type="button" size="sm">
                    {t('cuttysark.list.open')}
                  </Button>
                </Link>
                <Link to="/cuttysark/$sessionId/operate" params={{ sessionId: session.id }}>
                  <Button type="button" size="sm" variant="secondary">
                    {t('cuttysark.list.operate')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** @graph-connects none */
interface CreateSessionFormProps {
  onCancel: () => void;
}

/**
 * セッション作成フォーム。作ったら運営画面へ移って候補者と参加者を登録してもらう。
 * 入力の検証（四半期の形式など）は API が行い、メッセージをそのまま出す。
 * @graph-connects useCreateCuttysarkSession [calls] セッションの作成
 */
function CreateSessionForm({ onCancel }: CreateSessionFormProps): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateCuttysarkSession();
  const [title, setTitle] = useState('');
  const [quarter, setQuarter] = useState('');
  const [stage, setStage] = useState<CuttysarkStage>('team');

  return (
    <Card>
      <CardContent className="p-4">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { title, quarter, stage },
              {
                onSuccess: (view) => {
                  void navigate({
                    to: '/cuttysark/$sessionId/operate',
                    params: { sessionId: view.id },
                  });
                },
              },
            );
          }}
        >
          <div className="grid gap-3 md:grid-cols-3">
            <Input
              label={t('cuttysark.create.sessionTitle')}
              placeholder={t('cuttysark.create.sessionTitlePlaceholder')}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
            <Input
              label={t('cuttysark.create.quarter')}
              placeholder={t('cuttysark.create.quarterPlaceholder')}
              value={quarter}
              onChange={(e) => {
                setQuarter(e.target.value);
              }}
            />
            <Select
              label={t('cuttysark.create.stage')}
              options={CUTTYSARK_STAGES.map((value) => ({
                value,
                label: t(`cuttysark.stage.${value}`),
              }))}
              value={stage}
              onChange={(e) => {
                const next = CUTTYSARK_STAGES.find((s) => s === e.target.value);
                if (next !== undefined) setStage(next);
              }}
            />
          </div>
          {create.isError && (
            <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
              {create.error.message}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={create.isPending}>
              {t('cuttysark.create.submit')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              {t('cuttysark.create.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
